import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronRight, CalendarDays } from 'lucide-react';
import { rosterCoverage } from './managementLoadUtils';

/** Schedule coverage for decision-making (published vs draft). */
export default function ManagementScheduleSection({ roster, rangeLabel, onOpen }) {
  const cov = rosterCoverage(roster?.shifts || []);
  const upcoming = (roster?.shifts || []).slice(0, 12);
  const maxDay = Math.max(1, ...cov.days.map((d) => d.count));

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="mgmt-schedule-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Schedule</div>
          <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
            Duty coverage · {rangeLabel}
          </h3>
        </div>
        <Button variant="outline" size="sm" onClick={onOpen} data-testid="mgmt-roster-drilldown">
          Full roster <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { l: 'Covered days', v: cov.coveredDays },
          { l: 'Published shifts', v: cov.published },
          { l: 'Draft shifts', v: cov.draft },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border bg-zinc-50 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500">{s.l}</p>
            <p className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>{s.v}</p>
          </div>
        ))}
      </div>

      {cov.days.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Coverage intensity</p>
          <div className="flex flex-wrap gap-1">
            {cov.days.slice(0, 21).map((d) => {
              const pct = d.published / maxDay;
              const bg = d.published === 0 ? '#f4f4f5' : `rgba(241,90,34,${0.25 + pct * 0.75})`;
              return (
                <div
                  key={d.date}
                  title={`${d.date}: ${d.published} published / ${d.count} total`}
                  className="h-8 w-8 rounded-md border border-white shadow-sm"
                  style={{ background: bg }}
                />
              );
            })}
          </div>
        </div>
      )}

      {!upcoming.length ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-dashed rounded-lg p-4 flex items-start gap-2">
          <CalendarDays className="h-4 w-4 mt-0.5 shrink-0" />
          No shifts in this window. Publish duties from Roster to improve coverage visibility.
        </div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm" data-testid="mgmt-roster-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2">Date</th>
                <th className="text-left">Staff</th>
                <th className="text-left">Shift</th>
                <th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.map((s, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="py-2 text-zinc-700">{s.date}</td>
                  <td className="font-semibold text-zinc-900">{s.doctor}</td>
                  <td>{s.start}–{s.end}</td>
                  <td>
                    <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${
                      s.published !== false
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-amber-50 text-amber-800 border-amber-200'
                    }`}>
                      {s.published !== false ? 'Published' : 'Draft'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
